// GENERATED by helm-console/scripts/ensure-style-runtime.mjs — DO NOT EDIT.
// Verbatim copy of helm-bridge/src/client/glass-bokeh.ts. Regenerated every console
// build; edits here are overwritten. Owner correction 2026-09-07: the style
// substrate (backgrounds/canvas, reduce-effects) runs on the Bridge home too.
// glass-bokeh.ts — GLASS's animated ground: five badly-defocused discs of
// theme-tinted light, drifting behind the glass, plus a rare satellite pass.
//
// IT OWNS NO CANVAS. PR-4's style-axis.ts owns the element, the ONE-canvas
// cap, the rAF loop and pause-on-hidden; this module registers a PAINTER
// (`(ctx, t) => void`) and is called once per frame with the substrate's
// clock. That split is the reason scripts/perf-budget.mjs can prove the cap
// and the pause by driving the substrate — a style that rolled its own loop
// would be outside every one of those arms.
//
// WHAT IT DRAWS (spec §4). Five discs, radius 80–240 CSS px, each a
// createRadialGradient from a theme colour at PEAK ALPHA 0.10 (a hard cap,
// read from `--glass-disc-alpha` so the CSS "held exposure" still frame and
// the moving frame cannot disagree) to fully transparent. Each owns an
// independent sine path with periods in [60 s, 140 s] and amplitudes ≤ 12%
// of the viewport, so per frame a disc moves a fraction of a pixel and
// nothing ever crosses more than an eighth of the screen. No stars, no
// points, no sparkle: this is defocused light, not a particle field.
//
// THE SATELLITE PASS is scheduled on a random 20–40 s interval rather than
// looped: because it is rare it reads as a moment, and because it is
// scheduled it never syncs with anything else on screen. A 2 px line crosses
// one corner over 1.2 s with a 0 → 0.35 → 0 alpha ramp.
//
// WHY IT NEVER RUNS UNDER A REDUCE LANE: style-axis.ts's effectiveGrade()
// degrades `canvas` to `gradient` whenever prefers-reduced-motion OR
// prefers-reduced-transparency matches, so the element is never constructed
// and this painter is never called. styles.css paints the same five discs at
// t = 0 as a static background-image instead — the held exposure.

/** The hard alpha cap, mirrored here so a stylesheet that loses the token
 * cannot silently raise it. `--glass-disc-alpha` is the live value; this is
 * the ceiling it is clamped to. */
export const DISC_ALPHA_CEILING = 0.1;

/** How often the painter re-reads layout and theme tokens (ms). Both are
 * getComputedStyle / clientWidth reads — cheap, but not 60 times a second.
 * A resize or a theme switch therefore lands within one of these windows,
 * which for a background nobody is looking at directly is imperceptible and
 * costs ~2 layout reads per second instead of ~120. */
const REFRESH_MS = 500;

/** dpr is capped: a 3× backing store for a blurred gradient is pure cost. */
const MAX_DPR = 2;

interface Disc {
  /** fraction of viewport, 0..1 */ cx: number;
  cy: number;
  /** amplitude as a fraction of viewport (≤ .12) */ ax: number;
  ay: number;
  /** period, ms */ px: number;
  py: number;
  /** phase, radians */ ph: number;
  /** radius in CSS px */ r: number;
  /** which theme token tints it */ token: string;
  /** alpha scale relative to the cap (≤ 1) */ a: number;
}

/** The five discs. Fixed, not random: a background that is different on
 * every reload is a background nobody can review, and the CSS held-exposure
 * still frame has to be able to match it. */
const DISCS: Disc[] = [
  { cx: 0.18, cy: 0.26, ax: 0.10, ay: 0.06, px: 97_000, py: 131_000, ph: 0.0, r: 240, token: '--accent', a: 1.0 },
  { cx: 0.74, cy: 0.18, ax: 0.08, ay: 0.09, px: 121_000, py: 83_000, ph: 1.1, r: 200, token: '--aux-1', a: 1.0 },
  { cx: 0.62, cy: 0.76, ax: 0.11, ay: 0.07, px: 67_000, py: 139_000, ph: 2.3, r: 280, token: '--aux-2', a: 0.9 },
  { cx: 0.34, cy: 0.68, ax: 0.07, ay: 0.10, px: 113_000, py: 71_000, ph: 3.4, r: 180, token: '--sea', a: 1.0 },
  { cx: 0.90, cy: 0.56, ax: 0.09, ay: 0.08, px: 79_000, py: 127_000, ph: 4.6, r: 220, token: '--accent-2', a: 0.8 },
];

interface Env {
  at: number;
  w: number;
  h: number;
  dpr: number;
  alpha: number;
  colors: string[];
  streak: string;
}

let env: Env | null = null;
/** Next satellite pass, as a painter-clock timestamp. `-1` = not scheduled
 * yet (the first pass is scheduled on the first frame, so the clock's origin
 * does not matter). */
let nextPass = -1;
let passStart = -1;

function readEnv(canvas: HTMLCanvasElement, now: number): Env {
  const cs = getComputedStyle(document.documentElement);
  const tok = (name: string, fallback: string): string => cs.getPropertyValue(name).trim() || fallback;
  const declared = parseFloat(tok('--glass-disc-alpha', String(DISC_ALPHA_CEILING)));
  const alpha = Number.isFinite(declared) ? Math.min(Math.max(declared, 0), DISC_ALPHA_CEILING) : DISC_ALPHA_CEILING;
  const w = canvas.clientWidth || 1;
  const h = canvas.clientHeight || 1;
  const dpr = Math.min(typeof devicePixelRatio === 'number' ? devicePixelRatio : 1, MAX_DPR);
  const want = { w: Math.round(w * dpr), h: Math.round(h * dpr) };
  if (canvas.width !== want.w || canvas.height !== want.h) {
    canvas.width = want.w;
    canvas.height = want.h;
  }
  return {
    at: now,
    w,
    h,
    dpr,
    alpha,
    colors: DISCS.map((d) => tok(d.token, '#7f7f9f')),
    streak: tok('--accent-2', '#c3a0ff'),
  };
}

/** `#rrggbb` (or any resolved colour) + alpha → an rgba() the canvas can
 * take. Non-hex values (a colour name, an rgb()) are handed to the context
 * as-is with globalAlpha carrying the fade. */
function withAlpha(color: string, a: number): string {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  const h = m?.[1];
  if (!h) return color;
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/**
 * THE painter. Registered against the GLASS style id; the substrate calls it
 * once per animation frame with a monotonic clock.
 */
export function paintGlassBokeh(ctx: CanvasRenderingContext2D, t: number): void {
  const canvas = ctx.canvas as HTMLCanvasElement;
  if (!env || t - env.at > REFRESH_MS || t < env.at) env = readEnv(canvas, t);
  const { w, h, dpr, alpha, colors } = env;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  DISCS.forEach((d, i) => {
    const tint = colors[i] ?? '#7f7f9f';
    const x = (d.cx + d.ax * Math.sin((t / d.px) * Math.PI * 2)) * w;
    const y = (d.cy + d.ay * Math.sin((t / d.py) * Math.PI * 2 + d.ph)) * h;
    const g = ctx.createRadialGradient(x, y, 0, x, y, d.r);
    g.addColorStop(0, withAlpha(tint, alpha * d.a));
    g.addColorStop(1, withAlpha(tint, 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, d.r, 0, Math.PI * 2);
    ctx.fill();
  });

  // ---- the satellite pass -------------------------------------------
  if (nextPass < 0) nextPass = t + 20_000 + Math.random() * 20_000;
  if (passStart < 0 && t >= nextPass) passStart = t;
  if (passStart >= 0) {
    const k = (t - passStart) / 1200;
    if (k >= 1) {
      passStart = -1;
      nextPass = t + 20_000 + Math.random() * 20_000;
    } else {
      // 0 -> .35 -> 0
      const a = Math.sin(k * Math.PI) * 0.35;
      const x0 = w * 0.62;
      const y0 = -20;
      const x1 = w * 1.02;
      const y1 = h * 0.34;
      ctx.save();
      ctx.strokeStyle = withAlpha(env.streak, a);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x0 + (x1 - x0) * Math.max(0, k - 0.12), y0 + (y1 - y0) * Math.max(0, k - 0.12));
      ctx.lineTo(x0 + (x1 - x0) * k, y0 + (y1 - y0) * k);
      ctx.stroke();
      ctx.restore();
    }
  }
}

/** Test seam — the gate and the smoke drive the painter across arms. */
export function resetGlassBokeh(): void {
  env = null;
  nextPass = -1;
  passStart = -1;
}

/** The disc table, for the gate's ground arm: it composites a tier fill over
 * the WORST-CASE ground, which means the ground plus a disc at peak alpha. */
export function glassDiscTokens(): string[] {
  return DISCS.map((d) => d.token);
}
